"use client";

/**
 * 场景库 — 灯下道场里的「牌卡墙」
 *
 * 每张牌卡 = 一手真实牌局:米色纸卡落在绿毡上。
 * 「可训练」= decision.parsed,能直接上手练;「仅讲解」= 看教练拆这手。
 * 关键词搜索 + 客户端分页(每页 24 张),不再一次铺 338 张。
 * 资产3 不掺素材:牌卡上不出现作者与素材出处。
 */

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "motion/react";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Search,
  Spade,
  X,
} from "lucide-react";
import type { PilotScene } from "@/lib/scene-types";
import { conceptZh, positionZh, streetZh } from "@/lib/poker-terms-zh";
import { jargonZh } from "@/lib/jargon-zh";
import { parseCards } from "@/lib/poker-cards";
import { CardRow } from "@/components/playing-card";
import { DISPLAY_FONT, DojoFrame, Lift, Rise, Stagger } from "@/components/dojo";
import { cn } from "@/lib/cn";

const PAGE_SIZE = 24;

type Facets = {
  positions: string[];
  streets: string[];
  scenarios: string[];
};

type ScenesResponse = {
  total?: number;
  scenes?: PilotScene[];
  facets?: Facets;
  error?: string;
};

const EMPTY_FACETS: Facets = {
  positions: [],
  streets: [],
  scenarios: [],
};

type Filters = {
  scenario: string;
  position: string;
  street: string;
};

const EMPTY_FILTERS: Filters = {
  scenario: "",
  position: "",
  street: "",
};

const SELECT_CLS =
  "rounded-lg border border-[#26372C] bg-[#0C130F] px-2.5 py-2 text-sm text-stone-200 outline-none transition focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/20";

function FilterSelect({
  label,
  value,
  options,
  toLabel,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  toLabel?: (raw: string) => string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-2.5 text-xs font-medium text-stone-500">
      <span className="shrink-0">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={SELECT_CLS}
      >
        <option value="">全部</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {toLabel ? toLabel(o) : o}
          </option>
        ))}
      </select>
    </label>
  );
}

/** 米色牌卡:扑克桌上的座次卡 */
function SceneCard({ scene }: { scene: PilotScene }) {
  const { spot } = scene;
  const trainable = scene.decision?.parsed === true;
  const hand = spot.hero_hand ? parseCards(spot.hero_hand) : null;

  return (
    <Link
      href={`/scenes/${scene.scene_id}`}
      className="group block h-full rounded-[18px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]"
    >
      <div className="flex h-full flex-col rounded-[18px] bg-[linear-gradient(165deg,#F7F2E3,#ECE3CB)] p-5 shadow-[0_18px_44px_-16px_rgba(0,0,0,0.6)] ring-1 ring-black/40 transition-shadow duration-200 group-hover:shadow-[0_28px_60px_-18px_rgba(0,0,0,0.72),0_0_40px_-12px_rgba(228,184,91,0.28)]">
        {/* 牌卡顶部的筹码行:街 / 位置 / 场景(资产3 不掺素材,无作者) */}
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-medium">
          <span className="rounded-md bg-[#173427] px-2 py-0.5 text-[#A7DCC0]">
            {streetZh(spot.street)}
          </span>
          <span className="rounded-md bg-[#E4DCC4] px-2 py-0.5 text-[#5A6350]">
            {positionZh(spot.hero_position)} vs {positionZh(spot.villain_position)}
          </span>
          {(scene.concepts?.scenarios || []).map((sc) => (
            <span
              key={sc}
              className="rounded-md bg-[#EFE3C2] px-2 py-0.5 text-[#7A6430]"
            >
              {conceptZh(sc)}
            </span>
          ))}
        </div>

        <h2 className="mt-3 line-clamp-2 text-[15px] font-bold leading-snug text-[#1B231C]">
          {jargonZh(scene.title)}
        </h2>
        <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-[#6B6757]">
          {jargonZh(spot.decision_point)}
        </p>

        {hand && hand.length > 0 && (
          <div className="mt-3">
            <CardRow cards={hand} raw={spot.hero_hand} size="sm" />
          </div>
        )}

        {/* 卡脚:可训练徽章 + 教学分(街/位置中文 chip 已见卡顶,不重复英文小注) */}
        <div className="mt-auto flex items-center gap-2 pt-4">
          <span
            className={cn(
              "rounded-md px-2 py-0.5 text-[11px] font-semibold",
              trainable
                ? "bg-[#1D5C3F] text-[#D9F2E4]"
                : "bg-[#423E33]/10 text-[#6B6757]",
            )}
          >
            {trainable ? "可训练" : "仅讲解"}
          </span>
          <span className="rounded-md bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-800">
            教学分 {scene.score}
          </span>
          <span className="ml-auto text-emerald-700 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            <ArrowRight className="size-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function ScenesPage() {
  const [data, setData] = useState<ScenesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const reduce = useReducedMotion();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(filters)) {
          if (v) qs.set(k, v);
        }
        const url = qs.size > 0 ? `/api/scenes?${qs}` : "/api/scenes";
        const res = await fetch(url, { cache: "no-store" });
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setData({ error: String(e), scenes: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const scenes = useMemo(() => data?.scenes || [], [data]);
  const facets = data?.facets || EMPTY_FACETS;
  const hasFilter = useMemo(
    () => Object.values(filters).some(Boolean) || search.trim() !== "",
    [filters, search],
  );

  const trainableCount = useMemo(
    () => scenes.filter((s) => s.decision?.parsed === true).length,
    [scenes],
  );

  // 关键词在标题 / 决策点里找(服务端已按下拉筛过一轮;不搜作者——资产3 不掺素材)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scenes;
    return scenes.filter((s) =>
      [s.title, s.spot.decision_point]
        .join("\n")
        .toLowerCase()
        .includes(q),
    );
  }, [scenes, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageScenes = useMemo(
    () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filtered, safePage],
  );

  const applyFilter = (patch: Partial<Filters>) => {
    setLoading(true);
    setPage(1);
    setFilters((f) => ({ ...f, ...patch }));
  };

  return (
    <DojoFrame>
      <Stagger className="space-y-8">
        {/* 抬头:这面墙是什么 */}
        <Rise>
          <header className="space-y-3">
            <p className="text-[11px] font-semibold tracking-[0.24em] text-amber-300/70">
              GTO DOJO · 场景库
            </p>
            <h1
              className={cn(
                DISPLAY_FONT,
                "text-4xl font-extrabold tracking-tight text-stone-50 md:text-5xl",
              )}
            >
              牌桌场景库
            </h1>
            <p className="max-w-[56ch] text-sm leading-relaxed text-stone-400">
              每张牌卡都是一手真实牌局:看局面、选打法、听教练拆这手。
              标「可训练」的卡能直接上手练,「仅讲解」的卡看教练拆给你看。
            </p>
            <p className="font-mono text-xs tabular-nums text-stone-500">
              {loading && scenes.length === 0
                ? "正在数牌…"
                : `共 ${data?.total ?? scenes.length} 个场景 · 可训练 ${trainableCount} · 仅讲解 ${scenes.length - trainableCount}`}
            </p>
          </header>
        </Rise>

        {/* 工具条:关键词 + 筛选 */}
        <Rise>
          <div className="space-y-4 rounded-2xl border border-[#223329] bg-[#0D1510]/80 px-5 py-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-stone-500" />
              <input
                type="search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="搜一搜:诈唬、庄位、河牌、再加注…"
                aria-label="搜索场景"
                className="w-full rounded-xl border border-[#26372C] bg-[#0C130F] py-2.5 pl-10 pr-10 text-sm text-stone-200 outline-none transition placeholder:text-stone-600 focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/20"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                  aria-label="清空搜索"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-stone-500 transition hover:text-stone-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              <FilterSelect
                label="场景"
                value={filters.scenario}
                options={facets.scenarios}
                toLabel={conceptZh}
                onChange={(v) => applyFilter({ scenario: v })}
              />
              <FilterSelect
                label="位置"
                value={filters.position}
                options={facets.positions}
                toLabel={positionZh}
                onChange={(v) => applyFilter({ position: v })}
              />
              <FilterSelect
                label="街"
                value={filters.street}
                options={facets.streets}
                toLabel={streetZh}
                onChange={(v) => applyFilter({ street: v })}
              />
              {hasFilter && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    applyFilter(EMPTY_FILTERS);
                  }}
                  className="rounded-lg border border-[#2A3D30] px-3 py-1.5 text-xs text-stone-400 transition hover:border-emerald-400/40 hover:text-emerald-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
                >
                  清除筛选
                </button>
              )}
              <span className="ml-auto font-mono text-xs tabular-nums text-stone-500">
                {loading ? "洗牌中…" : `${filtered.length} 个结果`}
              </span>
            </div>
          </div>
        </Rise>

        {data?.error && (
          <Rise>
            <p className="rounded-xl border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">
              {data.error}
            </p>
          </Rise>
        )}

        {loading && scenes.length === 0 && (
          <Rise>
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-[#1E2E23] bg-[#0D1510]/60 py-16">
              <motion.span
                animate={reduce ? undefined : { opacity: [0.35, 1, 0.35] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                className="text-emerald-300/80"
              >
                <Spade className="size-6" />
              </motion.span>
              <p className="font-mono text-xs tabular-nums text-stone-500">
                正在摆牌桌…
              </p>
            </div>
          </Rise>
        )}

        {!loading && filtered.length === 0 && !data?.error && (
          <Rise>
            <div className="rounded-2xl border border-[#223329] bg-[#0D1510]/80 px-6 py-14 text-center">
              <p className="text-sm text-stone-400">
                没有匹配的牌局,换个关键词或筛选试试。
              </p>
              {hasFilter && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    applyFilter(EMPTY_FILTERS);
                  }}
                  className="mt-4 rounded-lg border border-[#2A3D30] px-4 py-2 text-xs text-stone-300 transition hover:border-emerald-400/40 hover:text-emerald-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
                >
                  清除全部筛选
                </button>
              )}
            </div>
          </Rise>
        )}

        {/* 牌卡墙:翻页即重新发牌 */}
        {pageScenes.length > 0 && (
          <Stagger key={safePage} className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {pageScenes.map((s) => (
              <Rise key={s.scene_id} className="h-full">
                <Lift className="h-full">
                  <SceneCard scene={s} />
                </Lift>
              </Rise>
            ))}
          </Stagger>
        )}

        {/* 分页 */}
        {pageCount > 1 && (
          <Rise>
            <nav
              aria-label="场景分页"
              className="flex items-center justify-between rounded-2xl border border-[#1E2E23] bg-[#0D1510]/80 px-4 py-3"
            >
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={cn(
                  "inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70",
                  safePage <= 1
                    ? "cursor-not-allowed text-stone-600"
                    : "text-stone-200 hover:bg-[#14231A] hover:text-emerald-200 active:scale-[0.98]",
                )}
              >
                <ChevronLeft className="size-4" />
                上一页
              </button>
              <span className="font-mono text-xs tabular-nums text-stone-500">
                第 <b className="text-stone-200">{safePage}</b> / {pageCount} 页 ·
                每页 {PAGE_SIZE} 张
              </span>
              <button
                type="button"
                disabled={safePage >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                className={cn(
                  "inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70",
                  safePage >= pageCount
                    ? "cursor-not-allowed text-stone-600"
                    : "text-stone-200 hover:bg-[#14231A] hover:text-emerald-200 active:scale-[0.98]",
                )}
              >
                下一页
                <ChevronRight className="size-4" />
              </button>
            </nav>
          </Rise>
        )}
      </Stagger>
    </DojoFrame>
  );
}
